import type { FC, ReactNode } from "react";
import type { Key } from "react-aria-components";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { useFieldContext } from "..";
import { FieldErrors } from "./FieldErrors";

interface ComboBoxFieldProps {
	label?: string;
	tooltip?: string;
	hint?: string;
	size?: "sm" | "md";
	placeholder?: string;
	placeholderIcon?: FC | ReactNode | null;
	popoverClassName?: string;
	items?: SelectItemType[];
	children: ReactNode | ((item: SelectItemType) => ReactNode);
	// Additional props for ComboBox functionality
	isDisabled?: boolean;
	allowsCustomValue?: boolean;
	field?: any; // Simplified for now - will be properly typed later
	/**
	 * Callback fired when a selection is made from the dropdown.
	 * Use this to auto-fill other form fields based on the selected item.
	 * @param key - The key (id) of the selected item
	 * @param item - The full item object if found in items array
	 */
	onItemSelect?: (key: Key, item?: SelectItemType) => void;
	/** Load more when user scrolls near bottom (infinite list) */
	onLoadMore?: () => void;
	hasNextPage?: boolean;
	isFetchingNextPage?: boolean;
}

const ComboBoxField = ({
	label,
	tooltip,
	hint,
	size = "sm",
	placeholder = "",
	placeholderIcon = null,
	popoverClassName,
	items,
	children,
	isDisabled,
	allowsCustomValue = false,
	field: fieldProp,
	onItemSelect,
	onLoadMore,
	hasNextPage,
	isFetchingNextPage,
	...props
}: ComboBoxFieldProps) => {
	// Support both patterns:
	// 1. form.Field with explicit field prop
	// 2. form.AppField with useFieldContext
	const contextField = useFieldContext<string>();
	const field = fieldProp || contextField;

	return (
		<div className="space-y-2">
			<Select.ComboBox
				{...props}
				label={label}
				tooltip={tooltip}
				hint={hint}
				size={size}
				placeholder={placeholder}
				// placeholderIcon={placeholderIcon} // TODO: Fix this prop
				popoverClassName={popoverClassName}
				items={items}
				isDisabled={isDisabled || field.state.meta.isValidating}
				isInvalid={field.state.meta.isTouched && !field.state.meta.isValid}
				isRequired={field.state.meta.isRequired}
				inputValue={field.state.value}
				onInputChange={(value) => {
					field.handleChange(value);
				}}
				onSelectionChange={(key) => {
					if (key) {
						// Find the selected item from the items array
						const selectedItem = items?.find(
							(item) => item.id === key || item.id === String(key),
						);
						// Update the field value to the item's label (display name)
						if (selectedItem) {
							field.handleChange(selectedItem.label);
						} else {
							field.handleChange(key as string);
						}
						// Call the callback for auto-filling other fields
						onItemSelect?.(key, selectedItem);
					}
				}}
				onBlur={field.handleBlur}
				name={field.name}
				allowsCustomValue={allowsCustomValue}
				onLoadMore={onLoadMore}
				hasNextPage={hasNextPage}
				isFetchingNextPage={isFetchingNextPage}
			>
				{children}
			</Select.ComboBox>
			<FieldErrors meta={field.state.meta} />
		</div>
	);
};

export default ComboBoxField;
